// Preset avatar renderer — maps 'preset:xxx' keys to styled initial tiles.
// Keeps the UI consistent across the app without external image dependencies.

const PRESET_META = {
    lion: { emoji: "🦁", gradient: ["#F5B041", "#B9770E"] },
    fox: { emoji: "🦊", gradient: ["#E67E22", "#A04000"] },
    owl: { emoji: "🦉", gradient: ["#8E44AD", "#5B2C6F"] },
    star: { emoji: "⭐", gradient: ["#F1C40F", "#9A7D0A"] },
    moon: { emoji: "🌙", gradient: ["#5D6D7E", "#283747"] },
    crescent: { emoji: "☪️", gradient: ["#D4A017", "#7D5A00"] },
    tulip: { emoji: "🌷", gradient: ["#E91E63", "#880E4F"] },
    palm: { emoji: "🌴", gradient: ["#27AE60", "#0E6655"] },
    lantern: { emoji: "🏮", gradient: ["#D35400", "#873600"] },
    mountain: { emoji: "⛰️", gradient: ["#7F8C8D", "#2C3E50"] },
    river: { emoji: "🌊", gradient: ["#2980B9", "#1A5276"] },
    book: { emoji: "📖", gradient: ["#8D6E63", "#4E342E"] },
    feather: { emoji: "🪶", gradient: ["#95A5A6", "#566573"] },
    anchor: { emoji: "⚓", gradient: ["#34495E", "#17202A"] },
    compass: { emoji: "🧭", gradient: ["#A569BD", "#4A235A"] },
};

function presetKey(avatar) {
    if (!avatar) return "lion";
    if (avatar.startsWith("preset:")) return avatar.slice(7);
    return null;
}

export function Avatar({ avatar, name = "", size = 72, rounded = "full", className = "", testid = null }) {
    const key = presetKey(avatar);
    const meta = key ? PRESET_META[key] : null;
    const px = typeof size === "number" ? `${size}px` : size;
    const radius = rounded === "full" ? "9999px" : "16px";

    if (meta) {
        return (
            <div
                className={`inline-flex items-center justify-center font-display select-none ${className}`}
                style={{
                    width: px,
                    height: px,
                    borderRadius: radius,
                    background: `linear-gradient(135deg, ${meta.gradient[0]} 0%, ${meta.gradient[1]} 100%)`,
                    fontSize: `${Math.max(16, parseInt(px) * 0.5)}px`,
                    lineHeight: 1,
                    boxShadow: "0 10px 24px rgba(0,0,0,0.25)",
                }}
                data-testid={testid}
                aria-label={name}
            >
                <span>{meta.emoji}</span>
            </div>
        );
    }

    if (avatar) {
        return (
            <img
                src={avatar}
                alt={name}
                className={`object-cover ${className}`}
                style={{ width: px, height: px, borderRadius: radius }}
                data-testid={testid}
            />
        );
    }

    // Fallback: initial on gold
    const initial = (name || "?").trim().charAt(0).toUpperCase();
    return (
        <div
            className={`inline-flex items-center justify-center font-display select-none ${className}`}
            style={{
                width: px,
                height: px,
                borderRadius: radius,
                background: "linear-gradient(135deg, #C9A458 0%, #7D5A00 100%)",
                color: "#1A120B",
                fontSize: `${Math.max(16, parseInt(px) * 0.42)}px`,
            }}
            data-testid={testid}
            aria-label={name}
        >
            {initial}
        </div>
    );
}

export const PRESET_AVATAR_KEYS = Object.keys(PRESET_META).map((k) => `preset:${k}`);
